/* eslint-disable @typescript-eslint/no-floating-promises */
import {SiteStore} from '@wix/wixstores-client-storefront-sdk';
import {clickOnCheckoutParams, errorInCheckoutParams} from '@wix/bi-logger-ecom-platform-data/v2/types';
import {PaymentMethod} from '@wix/wixstores-graphql-schema-node';
import {ContinueShoppingBiOrigin, EMPTY_CART_ID, PaymentMethodType} from '../../common/constants';
import {CartType, createOOIWebBILogger} from '@wix/wixstores-client-core';
import {
  getAdditionalFeesPrice,
  getCatalogAppIds,
  getItemsCount,
  getItemTypes,
  getNumberOfAdditionalFees,
  getValidations,
  stringifyPaymentMethods,
  toBiAmount,
} from '../utils/bi.utils';
import {
  cancelClickedInSelectYourShippingDestinationPopupSf,
  cartCartSideCartPresentedToUoUCartButtonSrc130Evid150,
  cartCartSideCartPresentedToUoUSrc130Evid149,
  cartCheckoutClickToOpenCloseMoreDetailsSrc130Evid190,
  cartEstimateTotalsErrorClickOnTryAgainSrc130Evid147,
  cartEstimateTotalsErrorShownSrc130Evid146,
  cartMinimumOrderMessageIsShownInCart,
  cartNotePresentedToTheUoUSrc130Evid157,
  cartPromoCodePresentedToTheUoUInCartSideCartSrc130Evid156,
  cartSideCartErrorPresentedInCartSideCartSrc130Evid131,
  clickOnAddNoteToSellerSf,
  clickOnCheckout,
  clickOnCheckoutWithEWallet,
  clickOnContinueShoppingFromCart,
  clickOnShippingDestinationInCartPageSf,
  clickToAddCoupon,
  clickToViewCartPage,
  deleteNoteToSellerSf,
  errorInCheckout,
  errorWhenApplyingACoupon,
  saveNoteToSellerSf,
  shippingShippingOptionsPresentedInCheckoutCartSrc130Evid183,
  shippingShippingOptionsSelectedSrc130Evid186,
  updateClickedInSelectYourShippingDestinationPopupSf,
  viewCartPageSf,
} from '@wix/bi-logger-ecom-platform-data/v2';
import {CartModel} from '../models/Cart.model';
import {EstimatedTotalsModel} from '../models/EstimatedTotals.model';
import {CartLayout} from '../../types/app.types';
import {SPECS} from '../specs';
import {DeliverySectionModel} from '../models/DeliverySection.model';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {Logger} from '@wix/web-bi-logger/dist/src/types';
import {IWidgetControllerConfig} from '@wix/native-components-infra';

type SetDestinationBIData = {
  cartId: string;
  cartType: string;
  shippingDestinationCountry?: string;
  shippingDestinationState?: string;
};

export class BIService {
  private readonly siteStore: SiteStore;
  private readonly origin: string;
  private readonly viewerContext: ViewerContext;
  private readonly biLogger: Logger;
  private readonly controllerConfig: IWidgetControllerConfig;

  constructor({
    siteStore,
    origin,
    viewerContext,
    controllerConfig,
  }: {
    siteStore: SiteStore;
    origin: string;
    viewerContext: ViewerContext;
    controllerConfig: IWidgetControllerConfig;
  }) {
    this.siteStore = siteStore;
    this.origin = origin;
    this.viewerContext = viewerContext;
    this.controllerConfig = controllerConfig;
    this.biLogger = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext)
      ? this.createBILogger(controllerConfig, viewerContext)
      : siteStore.webBiLogger;
  }

  private baseBiParams(cartModel: CartModel): {origin: string; cartId?: string; purchaseFlowId?: string} {
    return {
      origin: this.origin,
      cartId: !cartModel || cartModel.id === EMPTY_CART_ID ? undefined : cartModel.id,
      purchaseFlowId: cartModel?.purchaseFlowId,
    };
  }

  public clickOnShippingDestinationInCartPageSf(cartModel: CartModel, cartType: string): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      ...BIService.setDestinationBIData(cartModel, cartType),
    };

    this.biLogger.report(
      clickOnShippingDestinationInCartPageSf({
        ...biParams,
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: cartModel.checkoutId,
      })
    );
  }

  public clickOnCheckoutWithEWallet(
    cartModel: CartModel,
    estimatedTotals: EstimatedTotalsModel,
    cartType: string,
    /* istanbul ignore next */ paymentMethods: PaymentMethod[] = [],
    isWithOfflinePayment: boolean
  ): void {
    const params = {
      ...this.baseBiParams(cartModel),
      ...BIService.setDestinationBIData(cartModel, cartType),
      cartType,
      itemsCount: getItemsCount(cartModel),
      productsList: JSON.stringify(
        cartModel.lineItems.map((lineItem) => ({
          id: lineItem.catalogReference.catalogItemId,
          quantity: lineItem.quantity,
        }))
      ),
      ...(this.viewerContext.isExperimentEnabled(SPECS.StopUsingPaymentMethodsInCart)
        ? {}
        : {
            is_with_ewallet_payment: true,
            is_with_offline_payment: isWithOfflinePayment,
            num_of_paymet_providers: paymentMethods.length,
            paymet_providers: stringifyPaymentMethods(paymentMethods),
          }),
      additionalFeesPrice: getAdditionalFeesPrice(estimatedTotals),
      numberOfAdditionalFees: getNumberOfAdditionalFees(estimatedTotals),
      checkoutId: cartModel?.checkoutId,
      catalogAppId: getCatalogAppIds(cartModel),
    };

    this.biLogger.report(
      clickOnCheckoutWithEWallet({
        ...params,
      })
    );
  }

  public updateClickedInSelectYourShippingDestinationPopupSf(
    cartModel: CartModel,
    estimatedTotals: EstimatedTotalsModel,
    cartType: string,
    changedDestination: {country: string; subdivision: string; zipCode: string}
  ): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      shippingDestinationCountry: changedDestination.country,
      shippingDestinationState: changedDestination.subdivision,
      hasZipCode: !!changedDestination.zipCode,
      cartId: cartModel.id,
      cartType,
      additionalFeesPrice: getAdditionalFeesPrice(estimatedTotals),
      numberOfAdditionalFees: getNumberOfAdditionalFees(estimatedTotals),
    };

    this.biLogger.report(
      updateClickedInSelectYourShippingDestinationPopupSf({
        ...biParams,
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: cartModel.checkoutId,
      })
    );
  }

  public errorInCheckoutSf(cartModel: CartModel): void {
    const {contactInfo} = cartModel;
    const subdivision = contactInfo?.address.subdivision
      ? ` - ${contactInfo.address.subdivision}`
      : /* istanbul ignore next */ '';

    const destinationStr = `${contactInfo?.address.country}${subdivision}`;
    const params: errorInCheckoutParams = {
      ...this.baseBiParams(cartModel),
      field: 'destination',
      message: contactInfo ? `cannot ship to destination ${destinationStr}` : 'cannot ship to destination',
      stage: 'cart',
    };
    this.biLogger.report(errorInCheckout(params));
  }

  public cancelClickedInSelectYourShippingDestinationPopupSf(cartModel: CartModel, cartType: CartType): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      cartType,
    };

    this.biLogger.report(
      cancelClickedInSelectYourShippingDestinationPopupSf({
        ...biParams,
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: cartModel?.checkoutId,
      })
    );
  }

  public cartClickOnContinueShoppingSf(
    cartModel: CartModel,
    estimatedTotals: EstimatedTotalsModel,
    origin: ContinueShoppingBiOrigin
  ): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      origin,
      catalogAppId: getCatalogAppIds(cartModel),
      checkoutId: cartModel?.checkoutId,
      numberOfAdditionalFees: getNumberOfAdditionalFees(estimatedTotals),
      additionalFeesPrice: getAdditionalFeesPrice(estimatedTotals),
      itemType: getItemTypes(cartModel),
    };
    this.biLogger.report(clickOnContinueShoppingFromCart(biParams));
  }

  public clickToViewCartPage(cartModel: CartModel, cartType: string): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      origin: this.origin,
      itemsCount: getItemsCount(cartModel),
      cartType,
      catalogAppId: getCatalogAppIds(cartModel),
      checkoutId: cartModel?.checkoutId,
      isNavigateCart: true,
    };

    this.biLogger.report(clickToViewCartPage(biParams));
  }

  public minimumOrderMessageIsShownInCart(
    cartModel: CartModel,
    estimatedTotals: EstimatedTotalsModel,
    buttonEnabled: boolean
  ): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      currency: cartModel.currencyCode,
      minimumValueLong: toBiAmount(estimatedTotals.minimumOrderAmount.minAmount.convertedAmount),
      diffLong: toBiAmount(estimatedTotals.minimumOrderAmount.additionalAmount.convertedAmount),
      buttonEnabled,
      checkoutId: cartModel?.checkoutId,
      catalogAppId: getCatalogAppIds(cartModel),
    };

    this.biLogger.report(cartMinimumOrderMessageIsShownInCart(biParams));
  }

  public clickOnAddNoteToSellerSf(cartModel: CartModel): void {
    this.biLogger.report(
      clickOnAddNoteToSellerSf({
        ...this.baseBiParams(cartModel),
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: cartModel?.checkoutId,
      })
    );
  }

  public clickOnApplyPromotionalCodeSf(cartModel: CartModel): void {
    this.biLogger.report(
      clickToAddCoupon({
        ...this.baseBiParams(cartModel),
      })
    );
  }

  public errorWhenApplyingACouponSf(cartModel: CartModel, couponCode: string, errorCode: string): void {
    this.biLogger.report(
      errorWhenApplyingACoupon({
        ...this.baseBiParams(cartModel),
        couponCode,
        errorCode,
      })
    );
  }

  public errorPresentedInCartSideCart(errorMessage: string, cartModel: CartModel): void {
    this.biLogger.report(
      cartSideCartErrorPresentedInCartSideCartSrc130Evid131({
        origin: this.origin,
        errorMessage,
        purchaseFlowId: cartModel?.purchaseFlowId,
      })
    );
  }

  public updateBuyerNote = (cartModel: CartModel, hasNote: boolean): void => {
    const catalogAppId = getCatalogAppIds(cartModel);
    hasNote
      ? this.biLogger.report(
          saveNoteToSellerSf({
            ...this.baseBiParams(cartModel),
            is_empty: false,
            catalogAppId,
            checkoutId: cartModel.checkoutId,
          })
        )
      : this.biLogger.report(
          deleteNoteToSellerSf({
            ...this.baseBiParams(cartModel),
            catalogAppId,
            checkoutId: cartModel.checkoutId,
          })
        );
  };

  public viewCartPageSf({
    cartModel,
    estimatedTotals,
    cartType,
    paymentMethods,
    shouldShowCoupon,
    shouldShowBuyerNote,
    shouldShowContinueShopping,
    shouldShowShipping,
    shouldShowTax,
    hasPickupOption,
    cartLayout,
    isCheckoutButtonPresented,
    isViewCartButtonPresented,
    hasGiftCardLineItem,
  }: {
    cartModel: CartModel;
    estimatedTotals: EstimatedTotalsModel;
    cartType: string;
    paymentMethods?: PaymentMethod[];
    shouldShowCoupon: boolean;
    shouldShowBuyerNote: boolean;
    shouldShowContinueShopping: boolean;
    shouldShowShipping: boolean;
    shouldShowTax: boolean;
    hasPickupOption: boolean;
    cartLayout: string;
    isCheckoutButtonPresented: boolean;
    isViewCartButtonPresented: boolean;
    hasGiftCardLineItem: boolean;
  }): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      cartType,
      checkoutType: 'viewer',
      isContinueShopping: shouldShowContinueShopping,
      isDeliveryRates: shouldShowShipping,
      isPickupAvailable: hasPickupOption,
      isTaxCosts: shouldShowTax,
      is_note: shouldShowBuyerNote,
      is_promo_code: shouldShowCoupon,
      ...(this.viewerContext.isExperimentEnabled(SPECS.StopUsingPaymentMethodsInCart)
        ? {}
        : {
            is_with_ewallet_payment: paymentMethods.map((m) => m.type).includes(PaymentMethodType.eWallet),
            is_with_offline_payment: paymentMethods.map((m) => m.type).includes(PaymentMethodType.offline),
            num_of_paymet_providers: paymentMethods.length,
            paymet_providers: paymentMethods.map((m) => m.name).join(','),
          }),
      itemsCount: getItemsCount(cartModel),
      lineItemsCount: cartModel?.lineItems.length || 0,
      viewMode: this.viewerContext.getViewMode(),
      additionalFeesPrice: getAdditionalFeesPrice(estimatedTotals),
      numberOfAdditionalFees: getNumberOfAdditionalFees(estimatedTotals),
      savePaymentMethod: cartModel?.lineItems?.some((lineItem) => lineItem.savePaymentMethod),
      isCheckoutButtonPresented: cartLayout === CartLayout.SIDE_CART ? isCheckoutButtonPresented.toString() : 'true',
      isViewCartButtonPresented: cartLayout === CartLayout.SIDE_CART ? isViewCartButtonPresented.toString() : 'false',
      isPromoCodeDisabled:
        this.viewerContext.isExperimentEnabled(SPECS.GiftCardAddToCartSettings) && hasGiftCardLineItem,
    };
    this.biLogger.report(
      viewCartPageSf({
        ...biParams,
        cartLayout,
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: cartModel?.checkoutId,
        is_member: this.viewerContext.isExperimentEnabled(SPECS.GiftCardAddToCartSettings)
          ? this.controllerConfig.wixCodeApi.user.currentUser.loggedIn
          : !!this.siteStore.usersApi?.currentUser?.loggedIn,
        validationMessage: getValidations(estimatedTotals?.violations),
      })
    );
  }

  private static setDestinationBIData(cartModel: CartModel, cartType: string) {
    const biData: SetDestinationBIData = {
      cartId: cartModel.id,
      cartType,
    };
    if (cartModel.contactInfo) {
      biData.shippingDestinationCountry = cartModel.contactInfo.address.country;
      biData.shippingDestinationState = cartModel.contactInfo.address.subdivision;
    }
    return biData;
  }

  public cartEstimateTotalsErrorShown(cartModel: CartModel, cartLayout: string): void {
    this.biLogger.report(
      cartEstimateTotalsErrorShownSrc130Evid146({
        cartId: cartModel.id,
        cartLayout,
        purchaseFlowId: cartModel.purchaseFlowId,
      })
    );
  }

  public cartEstimateTotalsErrorClickOnTryAgain(cartModel: CartModel, cartLayout: string): void {
    this.biLogger.report(
      cartEstimateTotalsErrorClickOnTryAgainSrc130Evid147({
        cartId: cartModel.id,
        cartLayout,
        purchaseFlowId: cartModel.purchaseFlowId,
      })
    );
  }

  public shippingOptionsPresentedInCart(cartModel: CartModel, deliveryOptionsModel: DeliverySectionModel): void {
    this.biLogger.report(
      shippingShippingOptionsPresentedInCheckoutCartSrc130Evid183({
        origin: this.origin,
        cartId: cartModel.id,
        checkoutId: cartModel.checkoutId,
        purchaseFlowId: cartModel.purchaseFlowId,
        numOptionsPresented: deliveryOptionsModel?.options?.length,
        shippingOptionsPresented: deliveryOptionsModel?.optionsJson,
        isPartial: deliveryOptionsModel?.options?.some((option) => option.isPartial),
      })
    );
  }

  public shippingOptionsSelectedInCart(
    cartModel: CartModel,
    deliveryOptionsModel: DeliverySectionModel,
    shippingOptionId: string
  ): void {
    this.biLogger.report(
      shippingShippingOptionsSelectedSrc130Evid186({
        origin: this.origin,
        cartId: cartModel.id,
        checkoutId: cartModel.checkoutId,
        purchaseFlowId: cartModel.purchaseFlowId,
        numOptionsPresented: deliveryOptionsModel?.options?.length,
        shippingOptionsPresented: deliveryOptionsModel?.optionsJson,
        isPartial: deliveryOptionsModel?.options?.some((option) => option.isPartial),
        shippingOptionId,
        shippingOptionIndex: deliveryOptionsModel?.options?.findIndex((option) => option.id === shippingOptionId),
      })
    );
  }

  public reportClickToOpenCloseMoreDetails({
    cartModel,
    lineItemId,
    action,
  }: {
    cartModel: CartModel;
    lineItemId: string;
    action: string;
  }): void {
    void this.biLogger.report(
      cartCheckoutClickToOpenCloseMoreDetailsSrc130Evid190({
        purchaseFlowId: cartModel.purchaseFlowId,
        checkoutId: cartModel.checkoutId,
        lineItemId,
        cartId: cartModel.id,
        origin: this.origin,
        action,
      })
    );
  }

  public checkoutButtonPresentedToUoU(cartModel: CartModel, isButtonShown: boolean): void {
    void this.biLogger.report(
      cartCartSideCartPresentedToUoUSrc130Evid149({
        purchaseFlowId: cartModel.purchaseFlowId,
        cartLayout: this.origin,
        cartId: cartModel.id,
        isCheckoutButtonPresented: isButtonShown.toString(),
      })
    );
  }

  public cartButtonPresentedToUoU(cartModel: CartModel, isButtonShown: boolean): void {
    void this.biLogger.report(
      cartCartSideCartPresentedToUoUCartButtonSrc130Evid150({
        purchaseFlowId: cartModel.purchaseFlowId,
        cartLayout: this.origin,
        cartId: cartModel.id,
        isViewCartButtonPresented: isButtonShown.toString(),
      })
    );
  }

  public promoCodePresentedToUoU(cartModel: CartModel, isPromoCodeShown: boolean): void {
    this.biLogger.report(
      cartPromoCodePresentedToTheUoUInCartSideCartSrc130Evid156({
        purchaseFlowId: cartModel.purchaseFlowId,
        cartLayout: this.origin,
        cartId: cartModel.id,
        isPromoCodePresented: isPromoCodeShown,
      })
    );
  }

  public buyerNotePresentedToUoU(cartModel: CartModel, isBuyerNoteShown: boolean): void {
    this.biLogger.report(
      cartNotePresentedToTheUoUSrc130Evid157({
        purchaseFlowId: cartModel.purchaseFlowId,
        cartLayout: this.origin,
        cartId: cartModel.id,
        isnotepresented: isBuyerNoteShown,
      })
    );
  }

  public clickOnCheckout(bIData: clickOnCheckoutParams): void {
    this.biLogger.report(
      clickOnCheckout({
        ...bIData,
        is_member: this.controllerConfig.wixCodeApi.user.currentUser.loggedIn,
      })
    );
  }

  private createBILogger(controllerConfig: IWidgetControllerConfig, viewerContext: ViewerContext) {
    const bi = controllerConfig.platformAPIs.bi;
    const user = viewerContext.getViewMode() === 'Site' ? {visitor_id: bi.visitorId} : {uuid: bi.ownerId};
    return createOOIWebBILogger({
      biLoggerFactory: controllerConfig.platformAPIs.biLoggerFactory,
      user,
      biToken: bi.biToken,
      defaults: {
        storeId: viewerContext.getEcomPlatformInstanceId(),
        editorMode: viewerContext.getDeviceType() === 'Desktop' ? 'desktop' : 'mobile',
        isMerchant: false,
        appName: 'Stores',
        _msid: bi.metaSiteId,
      },
    });
  }
}
